import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'

interface ReportsByDay {
  date: string
  count: number
}

interface ProfessionalMetrics {
  id: string
  name: string
  patientsCount: number
  reportsCount: number
  insightsCount: number
}

interface StatusDistribution {
  status: string
  count: number
}

async function fetchReportsByDay(
  organizationId: string,
  days: number = 30,
): Promise<ReportsByDay[]> {
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - days)

  const { data, error } = await supabase
    .from('daily_reports')
    .select(`
      created_at,
      patients!inner (
        organization_id,
        status
      )
    `)
    .eq('patients.organization_id', organizationId)
    .neq('patients.status', 'deleted')
    .gte('created_at', startDate.toISOString())
    .order('created_at', { ascending: true })

  if (error) throw error

  // Group by date
  const grouped = (data ?? []).reduce(
    (acc, report: any) => {
      const date = report.created_at.split('T')[0]
      acc[date] = (acc[date] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  // Fill in missing dates
  const result: ReportsByDay[] = []
  const current = new Date(startDate)
  const end = new Date()

  while (current <= end) {
    const dateStr = current.toISOString().split('T')[0] as string
    result.push({
      date: dateStr,
      count: grouped[dateStr] ?? 0,
    })
    current.setDate(current.getDate() + 1)
  }

  return result
}

async function fetchProfessionalMetrics(
  organizationId: string,
): Promise<ProfessionalMetrics[]> {
  // Fetch organization members who are professionals
  const { data: members, error } = await supabase
    .from('organization_members')
    .select(`
      profile_id,
      role,
      profiles (
        id,
        full_name
      )
    `)
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .in('role', ['admin', 'professional'])

  if (error) throw error

  // For now, return placeholder metrics
  // In a real implementation, you'd join with patients assigned to each professional
  return (members ?? []).map((member: any) => ({
    id: member.profile_id,
    name: member.profiles?.full_name ?? 'Desconhecido',
    patientsCount: 0, // Would need patient-professional relationship via professional_assignments
    reportsCount: 0,
    insightsCount: 0,
  }))
}

async function fetchPatientsDistribution(
  organizationId: string,
): Promise<StatusDistribution[]> {
  const { data, error } = await supabase
    .from('patients')
    .select('status')
    .eq('organization_id', organizationId)
    .neq('status', 'deleted')

  if (error) throw error

  const grouped = (data ?? []).reduce(
    (acc, patient) => {
      acc[patient.status] = (acc[patient.status] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  return Object.entries(grouped).map(([status, count]) => ({
    status,
    count,
  }))
}

export function useReportsByDay(days: number = 30) {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['admin-reports-by-day', organizationId, days],
    queryFn: () => fetchReportsByDay(organizationId!, days),
    enabled: !!organizationId,
  })
}

export function useProfessionalMetrics() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['admin-professional-metrics', organizationId],
    queryFn: () => fetchProfessionalMetrics(organizationId!),
    enabled: !!organizationId,
  })
}

export function usePatientsDistribution() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['admin-patients-distribution', organizationId],
    queryFn: () => fetchPatientsDistribution(organizationId!),
    enabled: !!organizationId,
  })
}
